import type { NavigationItem } from '@/shared/config/navigation';
import { useLocation } from '@tanstack/react-router';

/** A nav item matches when the path equals its pattern or is nested under it. */
function matchesPattern(pathname: string, pattern: string): boolean {
  return pathname === pattern || pathname.startsWith(`${pattern}/`);
}

/**
 * The single active item is the one whose pattern matches the path MOST
 * specifically (longest matching pattern wins), so nested routes light up one
 * item rather than every ancestor.
 */
function resolveActiveHref(pathname: string, items: NavigationItem[]): string | null {
  let bestHref: string | null = null;
  let bestLen = -1;
  for (const item of items) {
    const pattern = item.activePattern ?? item.to;
    if (matchesPattern(pathname, pattern) && pattern.length > bestLen) {
      bestLen = pattern.length;
      bestHref = item.to;
    }
  }
  return bestHref;
}

/** The `to` of the nav item most specifically matching the current path (or `null`). */
export function useActiveNavHref(items: NavigationItem[]): string | null {
  const { pathname } = useLocation();
  return resolveActiveHref(pathname, items);
}
